// chat/intents — the chat intents: each a UI gesture mapped to ONE chat
// command (ui-as-projection: `intent: UIEvent → command`). The root resolves
// the facts chat does not own at intent time (the target material, the active
// context anchor, the conversation's standing, the composer's attached files)
// and puts them in the payload (chat-module-plan §1.3), so chat's `update`
// reads only its own Model. A plain factory: no hooks, no I/O.

import type * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import type { AuthorLocation } from "../../app/authorLocation.js";
import { sentAttachmentsFor } from "../../app/composerAttachments.js";
import type { IngestFile, IngestId } from "../../app/ingest.js";
import type { ChatFieldAnswer } from "../../components/Chat/types.js";
import type {
    ConversationId,
    MaterialRef,
    ResourceRef,
} from "../../components/Navigation/types.js";
import type { ChatCommand } from "./command.js";
import { chatSessionFor } from "./model.js";
import type { ChatState } from "./state.js";

export type ChatIntentDeps = {
    readonly sendChat: (command: ChatCommand) => void;
    readonly chatState: ChatState;
    readonly conversationIsDurable: (conversationId: string) => boolean;
    // F83: the ingest batch + its drop-time conversation linkage, so a send
    // carries the files attached to its composer (still uploading included).
    readonly ingest: readonly IngestFile[];
    readonly ingestConversations: ReadonlyMap<IngestId, string>;
    readonly activeMaterialId: string | null;
    readonly materialRefs: readonly MaterialRef[];
    readonly activeContextAnchor: Core.EntityRef.Ref | null;
    // The project the author is in (the Studio's route), or null: every turn
    // carries it so the assistant reads that project's brief.
    readonly activeProject: string | null;
    // Where the author is in the Studio (principle 22): the open page, the
    // caret, the selection and the scope chip; null with no page open.
    readonly activeLocation: AuthorLocation | null;
    readonly mkConversationId: () => ConversationId;
};

export type ChatIntents = {
    readonly submitText: (raw: string, conversationIdOverride?: string) => void;
    readonly retryTurn: (conversationId: string) => void;
    readonly stopTurn: (conversationIdOverride?: string) => void;
    readonly acceptOpeningMove: (conversationIdOverride?: string) => void;
    readonly draftMaterialFromSource: (source: ResourceRef) => void;
    readonly supplyFormAnswer: (
        conversationId: string,
        requestId: string,
        answers: readonly ChatFieldAnswer[],
    ) => void;
    readonly acceptProposal: (
        conversationId: string,
        proposalId: string,
    ) => void;
    readonly discardProposal: (
        conversationId: string,
        proposalId: string,
    ) => void;
};

export const mkChatIntents = ({
    sendChat,
    chatState,
    conversationIsDurable,
    ingest,
    ingestConversations,
    activeMaterialId,
    materialRefs,
    activeContextAnchor,
    activeProject,
    activeLocation,
    mkConversationId,
}: ChatIntentDeps): ChatIntents => {
    const selected = (override?: string): string | null =>
        override ?? chatState.selectedConversationId;

    // The port's turn optionally targets a material id (the mock keys
    // transcripts by it; the real server ignores it — its conversation is
    // global). From home/source/package tabs, fall back to the workspace's
    // first material; with none at all the target is null.
    const target = (): string | null =>
        activeMaterialId ?? materialRefs[0]?.id ?? null;

    // Send one compose turn with an explicit text. `onSubmit` sends the draft;
    // a suggested-response chip sends its own phrase directly (P40). Boundary
    // parse: an empty turn is rejected here, so chat only sees non-empty text.
    const submitText = (raw: string, conversationIdOverride?: string) => {
        const conversationId = selected(conversationIdOverride);
        const text = raw.trim();
        if (conversationId === null || text === "") return;
        const { composerMentions, pendingGrounding } = chatState;
        sendChat({
            type: "submitted_turn",
            conversationId,
            text,
            target: target(),
            anchor: activeContextAnchor,
            project: activeProject,
            location: activeLocation,
            // D4: the draft's @-picks are the REFERENCE relation, stamped onto
            // this message; F11 + F83: every file attached to THIS composer
            // (a nascent conversation's pre-send grounding, the drop-linked
            // uploads, landed or still uploading) joins them as a chip.
            mentions: [
                ...composerMentions,
                ...sentAttachmentsFor(
                    conversationId,
                    ingest,
                    ingestConversations,
                    pendingGrounding,
                    composerMentions,
                ),
            ],
            standing: conversationIsDurable(conversationId)
                ? "durable"
                : "nascent",
        });
    };

    // The composer's Stop button and Escape-while-drafting. Chat owns the
    // "nothing in flight" refusal (`stop_while_idle`).
    const stopTurn = (conversationIdOverride?: string) => {
        const conversationId = selected(conversationIdOverride);
        if (conversationId !== null)
            sendChat({ type: "requested_stop", conversationId });
    };

    // The aha bridge (onboarding-activation.md §5, item 1): the agent speaks
    // first.
    const acceptOpeningMove = (conversationIdOverride?: string) => {
        const conversationId = selected(conversationIdOverride);
        if (conversationId !== null)
            sendChat({ type: "requested_opening_move", conversationId });
    };

    // "Create material from this source": a fresh NASCENT conversation
    // grounded (optimistically) on the source, with NO durable write and NO
    // auto-send (hero-is-initial-conversation-state §2.2).
    const draftMaterialFromSource = (source: ResourceRef) =>
        sendChat({
            type: "requested_source_draft",
            conversationId: mkConversationId(),
            source,
        });

    // Answer the agent's ElicitationForm (LANE S3): persisted, then the turn
    // resumes (F12) with no author bubble (F113).
    const supplyFormAnswer = (
        conversationId: string,
        requestId: string,
        answers: readonly ChatFieldAnswer[],
    ) =>
        sendChat({
            type: "form_answer_submitted",
            conversationId,
            requestId,
            answers,
            target: target(),
            anchor: activeContextAnchor,
            project: activeProject,
        });

    // Retry after a failed turn (F113): re-send what was last sent — a typed
    // message as a fresh submit, a form resume as the SAME resume.
    const retryTurn = (conversationId: string) => {
        const { lastSent } = chatSessionFor(chatState.sessions, conversationId);
        switch (lastSent.type) {
            case "nothing_sent":
                return;
            case "last_sent_text":
                submitText(lastSent.text, conversationId);
                return;
            case "last_sent_form_resume":
                sendChat({
                    type: "retried_form_resume",
                    conversationId,
                    requestId: lastSent.requestId,
                    text: lastSent.text,
                    target: target(),
                    anchor: activeContextAnchor,
                    project: activeProject,
                });
                return;
            default:
                return exhaustSwitchCase(lastSent);
        }
    };

    return {
        submitText,
        retryTurn,
        stopTurn,
        acceptOpeningMove,
        draftMaterialFromSource,
        supplyFormAnswer,
        ...mkProposalIntents(sendChat),
    };
};

// A proposal's card: accept it here (the shortcut), or discard it. Needs only
// `sendChat`, so a host can wire it without the whole intent set.
export const mkProposalIntents = (
    sendChat: (command: ChatCommand) => void,
): Pick<ChatIntents, "acceptProposal" | "discardProposal"> => ({
    acceptProposal: (conversationId, proposalId) =>
        sendChat({
            type: "proposal_accept_requested",
            conversationId,
            proposalId,
        }),
    discardProposal: (conversationId, proposalId) =>
        sendChat({
            type: "proposal_discard_requested",
            conversationId,
            proposalId,
        }),
});
